import React, { useState } from "react";
import { Input } from "reactstrap";
import PropTypes from "prop-types";

import { CustomFormInput } from "../../../utils/FormFields";
import { showToast } from "../../../utils/Helper";
import CircularProgress from "../../CircularProgress/CircularProgress";

function MainAdvisorField({
  accountId,
  repId,
  mainAdvisorId,
  mainAdvisorName,
  canEdit,
  services,
  onChange,
}) {
  const [editing, setEditing] = useState(false);
  const [loadingAdvisors, setLoadingAdvisors] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [advisors, setAdvisors] = useState([]);
  const [selectedAdvisorId, setSelectedAdvisorId] = useState(mainAdvisorId);
  const [saving, setSaving] = useState(false);

  const startEdit = () => {
    setEditing(true);
    setSelectedAdvisorId(mainAdvisorId);
    setLoadError(false);
    setLoadingAdvisors(true);
    services.getRepAdvisors({ repId }, (response) => {
      setLoadingAdvisors(false);
      if (!response) {
        setLoadError(true);
        return;
      }
      setAdvisors(
        response.map((a) => ({
          id: a.advisorId !== undefined ? a.advisorId : a.AdvisorId,
          name: a.advisorName !== undefined ? a.advisorName : a.AdvisorName,
        }))
      );
    });
  };

  const cancelEdit = () => {
    setEditing(false);
    setSelectedAdvisorId(mainAdvisorId);
  };

  const confirmEdit = () => {
    if (String(selectedAdvisorId) === String(mainAdvisorId)) {
      setEditing(false);
      return;
    }
    const selectedAdvisor = advisors.find((a) => String(a.id) === String(selectedAdvisorId));
    setSaving(true);
    services.updateAccountMainAdvisor({ accountId, advisorId: selectedAdvisorId }, (response) => {
      setSaving(false);
      if (!response) {
        showToast("Error updating Main Advisor", true);
        return;
      }
      onChange("MainAdvisorId", selectedAdvisorId, selectedAdvisor ? selectedAdvisor.name : undefined);
      setEditing(false);
    });
  };

  if (!editing) {
    return (
      <CustomFormInput label="Main Advisor: ">
        {mainAdvisorName}
        {canEdit && (
          <i
            className="fa fa-pencil-square-o"
            aria-hidden="true"
            style={{ cursor: "pointer", marginLeft: 8 }}
            onClick={startEdit}
          />
        )}
      </CustomFormInput>
    );
  }

  return (
    <CustomFormInput label="Main Advisor: ">
      {loadingAdvisors ? (
        <CircularProgress sm />
      ) : (
        <React.Fragment>
          {loadError && <div className="text-danger mb-1">Error loading advisors</div>}
          {!loadError && advisors.length === 0 && (
            <div className="text-danger mb-1">No advisors available for this Rep</div>
          )}
          <Input
            type="select"
            disabled={saving || loadError || advisors.length === 0}
            value={selectedAdvisorId}
            onChange={(e) => setSelectedAdvisorId(e.target.value)}
          >
            {advisors.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </Input>
          <i
            className="fa fa-check"
            aria-hidden="true"
            style={{
              cursor: saving || loadError || advisors.length === 0 ? "default" : "pointer",
              marginLeft: 8,
              opacity: saving || loadError || advisors.length === 0 ? 0.5 : 1,
            }}
            onClick={saving || loadError || advisors.length === 0 ? undefined : confirmEdit}
          />
          <i
            className="fa fa-times"
            aria-hidden="true"
            style={{ cursor: saving ? "default" : "pointer", marginLeft: 8, opacity: saving ? 0.5 : 1 }}
            onClick={saving ? undefined : cancelEdit}
          />
        </React.Fragment>
      )}
    </CustomFormInput>
  );
}

MainAdvisorField.propTypes = {
  accountId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  repId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  mainAdvisorId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  mainAdvisorName: PropTypes.string,
  canEdit: PropTypes.bool.isRequired,
  services: PropTypes.shape({
    getRepAdvisors: PropTypes.func.isRequired,
    updateAccountMainAdvisor: PropTypes.func.isRequired,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
};

MainAdvisorField.defaultProps = {
  mainAdvisorId: undefined,
  mainAdvisorName: undefined,
};

export default MainAdvisorField;
